import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  TouchableOpacity,
  TextInput,
  Dimensions,
  ViewStyle,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import DLODropdown from '../DLODropdown';
import { DLODropdownOption } from '../../types/dlo.types';

// Icons from the Figma design
const FirstPageIcon = ({ color = '#005CBC' }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path d="M15.3417 13.825L11.5167 10L15.3417 6.175L14.1667 5L9.16667 10L14.1667 15L15.3417 13.825ZM5 5H6.66667V15H5V5Z" fill={color} />
  </Svg>
);

const KeyboardArrowLeftIcon = ({ color = '#005CBC' }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path d="M12.8417 13.825L9.02502 10L12.8417 6.175L11.6667 5L6.66669 10L11.6667 15L12.8417 13.825Z" fill={color} />
  </Svg>
);

const KeyboardArrowRightIcon = ({ color = '#005CBC' }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path d="M7.15833 13.825L10.975 10L7.15833 6.175L8.33333 5L13.3333 10L8.33333 15L7.15833 13.825Z" fill={color} />
  </Svg>
);

const LastPageIcon = ({ color = '#005CBC' }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path d="M4.65833 6.175L8.48333 10L4.65833 13.825L5.83333 15L10.8333 10L5.83333 5L4.65833 6.175ZM13.3333 5H15V15H13.3333V5Z" fill={color} />
  </Svg>
);

// Page Input Component
interface PageInputProps {
  value: number;
  onValueChange: (value: number) => void;
  totalPages: number;
}

const PageInput: React.FC<PageInputProps> = ({ value, onValueChange, totalPages }) => {
  const [inputValue, setInputValue] = useState(value.toString());

  const handleSubmit = () => {
    const newPage = parseInt(inputValue, 10);
    if (!isNaN(newPage) && newPage >= 1 && newPage <= totalPages) {
      onValueChange(newPage);
    } else {
      setInputValue(value.toString()); // Reset to current value if invalid
    }
  };

  return (
    <View style={styles.pageInputContainer}>
      <TextInput
        style={styles.pageInput}
        value={inputValue}
        onChangeText={setInputValue}
        onBlur={handleSubmit}
        onSubmitEditing={handleSubmit}
        keyboardType="number-pad"
        textAlign="center"
        maxLength={3}
      />
    </View>
  );
};

// Navigation Button Component
interface NavButtonProps {
  icon: React.ReactNode;
  onPress: () => void;
  disabled?: boolean;
}

const NavButton: React.FC<NavButtonProps> = ({ icon, onPress, disabled = false }) => (
  <TouchableOpacity 
    style={[styles.navButton, disabled && styles.navButtonDisabled]} 
    onPress={onPress}
    activeOpacity={disabled ? 1 : 0.7}
    disabled={disabled}
  >
    {icon}
  </TouchableOpacity>
);

// Main Component Props
export interface DLOScheduleTablePaginationProps {
  currentPage?: number;
  totalPages?: number;
  rowsPerPage?: number;
  rowsPerPageOptions?: number[];
  totalItems?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  style?: ViewStyle;
}

const DLOScheduleTablePagination: React.FC<DLOScheduleTablePaginationProps> = ({
  currentPage = 12,
  totalPages = 120,
  rowsPerPage = 10,
  rowsPerPageOptions = [5, 10, 25, 50, 100],
  totalItems,
  onPageChange,
  onRowsPerPageChange,
  style,
}) => {
  const screenWidth = Dimensions.get('window').width;
  const isSmallScreen = screenWidth < 768;
  const isMobile = screenWidth < 480;

  const handleFirstPage = () => {
    if (currentPage > 1) {
      onPageChange?.(1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange?.(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange?.(currentPage + 1);
    }
  };

  const handleLastPage = () => {
    if (currentPage < totalPages) {
      onPageChange?.(totalPages);
    }
  };

  const handlePageChange = (page: number) => {
    onPageChange?.(page);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    onRowsPerPageChange?.(newRowsPerPage);
  };

  // Convert number options to DLODropdownOption format
  const dropdownOptions: DLODropdownOption[] = rowsPerPageOptions.map(option => ({
    id: option.toString(),
    label: option.toString(),
    value: option.toString(),
  }));

  // Find current selected option
  const selectedOption = dropdownOptions.find(option => 
    parseInt(option.value, 10) === rowsPerPage
  ) || null;

  // Handle dropdown selection
  const handleDropdownSelect = (option: DLODropdownOption | DLODropdownOption[] | null) => {
    if (option && !Array.isArray(option)) {
      const newRowsPerPage = parseInt(option.value, 10);
      handleRowsPerPageChange(newRowsPerPage);
    }
  };

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  return (
    <View style={[styles.container, style]}>
      {/* Left Section - Rows per page */}
      <View style={styles.leftSection}>
        <Text style={styles.label}>Rows per page</Text>
        <DLODropdown
          value={selectedOption}
          options={dropdownOptions}
          onSelect={handleDropdownSelect}
          size="small"
          showLabel={false}
          minDropdownWidth={50}
          disableSorting={true}
          hideSearch={true}
          placement="bottom"
          offset={-4}
          removeMarginTop={true}
          style={styles.rowsPerPageDropdown}
        />
      </View>

      {/* Right Section - Navigation */}
      <View style={[styles.rightSection, isMobile && styles.rightSectionMobile]}>
        {/* Navigation Buttons - Left */}
        <View style={styles.navButtonsLeft}>
          <NavButton
            icon={<FirstPageIcon color={isFirstPage ? '#C0C0C0' : '#005CBC'} />}
            onPress={handleFirstPage}
            disabled={isFirstPage}
          />
          <NavButton
            icon={<KeyboardArrowLeftIcon color={isFirstPage ? '#C0C0C0' : '#005CBC'} />}
            onPress={handlePreviousPage}
            disabled={isFirstPage}
          />
        </View>

        {/* Page Information */}
        <View style={[styles.pageInfo, isMobile && styles.pageInfoMobile]}>
          <Text style={styles.label}>Page</Text>
          <PageInput
            value={currentPage}
            onValueChange={handlePageChange}
            totalPages={totalPages}
          />
          <Text style={styles.pageTotal}>
            <Text style={styles.pageText}>of </Text>
            <Text style={styles.pageTotalNumber}>{totalPages}</Text>
          </Text>
        </View>

        {/* Navigation Buttons - Right */}
        <View style={styles.navButtonsRight}>
          <NavButton
            icon={<KeyboardArrowRightIcon color={isLastPage ? '#C0C0C0' : '#005CBC'} />}
            onPress={handleNextPage}
            disabled={isLastPage}
          />
          <NavButton
            icon={<LastPageIcon color={isLastPage ? '#C0C0C0' : '#005CBC'} />}
            onPress={handleLastPage}
            disabled={isLastPage}
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 8,
    backgroundColor: '#FBFCFE',
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    position: 'relative',
  },
  label: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
  },
  rowsPerPageDropdown: {
    width: 60,
    minWidth: 60,
    zIndex: 1000,
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
  },
  rightSectionMobile: {
    gap: 12,
    flexWrap: 'wrap',
  },
  navButtonsLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  navButtonsRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pageInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pageInfoMobile: {
    gap: 4,
  },
  pageInputContainer: {
    width: 43,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#C0C0C0',
    backgroundColor: '#FBFCFE',
    padding: 1,
  },
  pageInput: {
    minHeight: 32,
    paddingHorizontal: 8,
    textAlign: 'center',
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    backgroundColor: '#FBFCFE',
  },
  pageTotal: {
    flexDirection: 'row',
  },
  pageText: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
  },
  pageTotalNumber: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
  },
  navButton: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
  },
  navButtonDisabled: {
    opacity: 0.5,
  },
});

export default DLOScheduleTablePagination;
